// prettier-ignore
class PredictiveSearch extends SearchForm {
	constructor() {
		super();
		this.cachedResults = new Map();
		this.predictiveSearchResults = this.querySelector(
			"[data-predictive-search]",
		);
		this.allPredictiveSearchInstances =
			document.querySelectorAll("predictive-search");
		this.isOpen = false;
		this.abortController = new AbortController();
		this.requestId = 0;
		this.searchTerm = "";
		this.body = document.querySelector("body");
		this.header = document.querySelector(".shopify-section-header");
		this.headerBottom = document.querySelector(".header__bottom");
		this.promoModal = document.querySelector(".search__modal");
		this.collectionList = document.querySelector(".template-404__collections");
		this.headerInput = document.querySelector(".header__search .search__input");
		this.calcHeight();
		this.setupEventListeners();
	}

	calcHeight() {
		if (this.header && document.querySelector(".predictive-search")) {
			document.documentElement.style.setProperty(
				"--search-height",
				`calc(100dvh - ${this.header.getBoundingClientRect().top}px)`,
			);
		}
	}

	setupEventListeners() {
		this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));
		this.input.addEventListener("focus", this.onFocus.bind(this));
		this.addEventListener("focusout", this.onFocusOut.bind(this));
		this.addEventListener("keyup", this.onKeyup.bind(this));
		// 方向键必须在 keydown 拦：keyup 时光标已经跳到输入框首尾、页面也已经滚动了
		this.addEventListener("keydown", this.onKeydown.bind(this));
	}

	onKeydown(event) {
		if (event.code !== "ArrowDown" && event.code !== "ArrowUp") return;
		if (!this.hasCurrentResults() || !this.querySelector('[role="option"]')) return;

		event.preventDefault();
		this.moveSelection(event.code === "ArrowDown" ? 1 : -1);
	}

	// 移动端用 display:none 收了每组的条数，那些条目不能被键盘选中
	getVisibleOptions() {
		return Array.from(this.querySelectorAll('[role="option"]')).filter(
			(option) => option.offsetParent !== null,
		);
	}

	moveSelection(step) {
		const options = this.getVisibleOptions();
		if (!options.length) return;

		const current = options.findIndex(
			(option) => option.getAttribute("aria-selected") === "true",
		);

		let next;
		if (current === -1) {
			// 还没选中任何一项：往下从第一条开始，往上从最后一条开始
			next = step > 0 ? 0 : options.length - 1;
		} else {
			next = (current + step + options.length) % options.length;
		}

		this.setSelection(options[next]);
	}

	setSelection(option) {
		this.querySelectorAll('[aria-selected="true"]').forEach((selected) => {
			selected.setAttribute("aria-selected", "false");
		});

		if (!option) {
			this.input.setAttribute("aria-activedescendant", "");
			return;
		}

		option.setAttribute("aria-selected", "true");
		// combobox 的焦点始终留在输入框，靠 aria-activedescendant 告诉读屏软件当前是哪一项
		this.input.setAttribute("aria-activedescendant", option.id);
		option.scrollIntoView({ block: "nearest" });
	}

	getQuery() {
		return this.input.value.trim();
	}

	// 续写关键词时旧结果会留在 DOM 里等新响应（防抖期间也一样），它们不能被方向键/Enter 选中。
	hasCurrentResults() {
		return this.getAttribute("results") === "true" && this.getQuery() === this.searchTerm;
	}

	onChange() {
		const newSearchTerm = this.getQuery();
		if (newSearchTerm !== this.searchTerm) {
			// 输入已变化，旧选中项不能继续被 Enter 激活，也不能把旧结果当作新查询的缓存。
			this.setSelection(null);
			this.removeAttribute("results");
		}
		if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
			// Remove the results when they are no longer relevant for the new search term
			// so they don't show up when the dropdown opens again
			this.querySelector(".predictive-search__results-groups-wrapper")?.remove();
		}

		// Update the term asap, don't wait for the predictive search query to finish loading
		this.updateSearchForTerm(this.searchTerm, newSearchTerm);

		this.searchTerm = newSearchTerm;

		if (!this.searchTerm.length) {
			this.close(true);
			return;
		}

		this.getSearchResults(this.searchTerm);
		if (this.getQuery().length) {
			this.hidePromoBlock();
		} else {
			this.showPromoBlock();
		}
	}

	onFormSubmit(event) {
		if (!this.hasCurrentResults()) this.setSelection(null);
		if (
			!this.getQuery().length ||
			this.querySelector('[aria-selected="true"] a') ||
			event.submitter?.classList.contains("card__link")
		)
			event.preventDefault();
	}

	onFormReset(event) {
		// 清空按钮触发的 reset 必须清除键盘选择；正常提交仍沿用 SearchForm 的选择语义。
		this.setSelection(null);
		super.onFormReset(event);
		if (super.shouldResetForm()) {
			this.searchTerm = "";
			this.abortController.abort();
			this.abortController = new AbortController();
			this.close(true);
		}
	}

	onFocus() {
		const currentSearchTerm = this.getQuery();
		if (this.classList.contains("search-modal__form")) {
			if (this.headerBottom) {
				this.headerBottom.classList.add("header__bottom--visible");
			}
			if (this.promoModal) {
				this.promoModal.classList.remove("search__modal--hidden");
			}
			if (this.collectionList) {
				this.collectionList.classList.remove("hidden");
			}
		}

		if (!currentSearchTerm.length) {
			return;
		}

		if (this.searchTerm !== currentSearchTerm) {
			// Search term was changed from other search input, treat it as a user change
			this.onChange();
		} else if (this.getAttribute("results") === "true") {
			this.open();
			if (this.promoModal) {
				this.promoModal.classList.add("search__modal--hidden");
			}
			if (this.collectionList) {
				this.collectionList.classList.add("hidden");
			}
		} else {
			this.getSearchResults(this.searchTerm);
		}
	}

	onFocusOut() {
		if (this.classList.contains("search-modal__form")) {
			if (this.headerBottom) {
				this.headerBottom.classList.remove("header__bottom--visible");
			}
		}
		if (!this.classList.contains("search-modal__form")) {
			// 失焦只收下拉，不清用户打的字（以前 close(true) 会清空）：iOS 键盘的 ✓/完成 只是让输入框失焦，
			// 收键盘是为了看被挡住的结果。手机全屏搜索面板开着时结果也留着可以继续滑，由 Cancel 关面板时再清。
			setTimeout(() => {
				if (this.contains(document.activeElement) || this.closest(".is-sheet-open")) return;
				this.close();
			});
		}
	}

	onBlur() {
		this.headerInput?.blur();
	}

	onKeyup(event) {
		if (!this.getQuery().length) this.close(true);
		event.preventDefault();

		switch (event.code) {
			case "Enter":
				event.preventDefault();
				this.selectOption();
				break;
			case "Escape":
				// close(true) 内部已经调过 closeResults(true)，原来这里多的一行
				// 用了未定义的 clearSearchTerm，每次按 Esc 都抛 ReferenceError。
				this.close(true);
				if (this.getQuery().length) {
					this.hidePromoBlock();
				} else {
					this.showPromoBlock();
				}
				break;
		}
	}

	updateSearchForTerm(previousTerm, newTerm) {
		const searchForTextElement = this.querySelector(
			"[data-predictive-search-search-for-text]",
		);
		const currentButtonText = searchForTextElement?.innerText;
		if (currentButtonText && previousTerm) {
			if (currentButtonText.split(previousTerm).length > 2) {
				// The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
				return;
			}
			const newButtonText = currentButtonText.replace(previousTerm, () => newTerm);
			searchForTextElement.innerText = newButtonText;
		}
		if (this.getQuery().length) {
			this.hidePromoBlock();
		} else {
			this.showPromoBlock();
		}
	}

	selectOption() {
		if (!this.hasCurrentResults()) return;
		const selectedOption = this.querySelector(
			'[aria-selected="true"] a, button[aria-selected="true"]',
		);

		if (selectedOption) selectedOption.click();
	}

	getSearchResults(searchTerm) {
		clearTimeout(this.searchRequestTimer);
		// 输入包含 [、( 等字符时不能当作正则；空格与连字符也不能共用缓存键。
		const queryKey = searchTerm.trim().toLowerCase();
		this.abortController.abort();
		this.abortController = new AbortController();
		const requestId = ++this.requestId;
		this.setLiveRegionLoadingState();

		const cached = this.cachedResults.get(queryKey);
		if (cached && cached.expires > Date.now()) {
			// 命中时移到末尾，淘汰最久未使用的查询。
			this.cachedResults.delete(queryKey);
			this.cachedResults.set(queryKey, cached);
			this.renderSearchResults(cached.html);
			//this.clickSearchTabs();
			this.calcHeight();
			return;
		}

		this.cachedResults.delete(queryKey);
		if (this.promoModal && this.classList.contains("search-modal__form")) {
			this.promoModal.classList.add("search__modal--hidden");
		}

		if (this.collectionList) {
			this.collectionList.classList.add("hidden");
		}

		// 超时直接结束当前 UI 等待，即使第三方 fetch 包装忽略 abort 也不会一直转圈。
		const timer = this.searchRequestTimer = setTimeout(() => {
			if (requestId !== this.requestId) return;
			this.close(); // 保留搜索词，仍可提交原生搜索；同时让迟到响应失效。
			console.warn('Predictive search timed out');
		}, 10000);

		fetch(
			// resources[limit] 上限就是 10，写更大的数会被 Shopify 直接截断；每组要显示几条由 section 里的 limit 控制。
			// 不要 query（搜索词联想）：点它只是再跳一次搜索结果页，反而把购买路径拉长了。
			`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search&resources[type]=product,collection,article,page&resources[limit]=10&resources[limit_scope]=each`,
			{ signal: this.abortController.signal },
		)
			.then((response) => {
				if (!response.ok) {
					throw new Error(response.status);
				}
				return response.text();
			})
			.then((text) => {
				if (requestId !== this.requestId || this.getQuery() !== searchTerm) return;
				const resultsSection = new DOMParser()
					.parseFromString(text, "text/html")
					.querySelector("#shopify-section-predictive-search");
				if (!resultsSection) throw new Error('Missing predictive search section');
				const resultsMarkup = resultsSection.innerHTML;
				// Save bandwidth keeping the cache in all instances synced
				this.allPredictiveSearchInstances.forEach(
					(predictiveSearchInstance) => {
						const cache = predictiveSearchInstance.cachedResults;
						const now = Date.now();
						for (const [key, entry] of cache) {
							if (entry.expires <= now) cache.delete(key);
						}
						cache.delete(queryKey);
						cache.set(queryKey, { html: resultsMarkup, expires: now + 60000 });
						if (cache.size > 20) cache.delete(cache.keys().next().value);
					},
				);
				this.renderSearchResults(resultsMarkup);
				//this.clickSearchTabs();
				this.calcHeight();
			})
			.catch((error) => {
				if (error?.name === 'AbortError' || error?.code === 20 || requestId !== this.requestId || this.getQuery() !== searchTerm) {
					// Code 20 means the call was aborted
					return;
				}
				this.close();
				// 建议接口失败时仍允许原生搜索表单提交，不制造未处理的 Promise 异常。
				console.warn('Predictive search unavailable', error);
			})
			.finally(() => clearTimeout(timer));

		if (this.getQuery().length) {
			this.hidePromoBlock();
		} else {
			this.showPromoBlock();
		}
	}

	setLiveRegionLoadingState() {
		this.statusElement =
			this.statusElement || this.querySelector(".predictive-search-status");
		this.loadingText =
			this.loadingText || this.getAttribute("data-loading-text");

		this.setLiveRegionText(this.loadingText);
		this.setAttribute("loading", true);
	}

	setLiveRegionText(statusText) {
		if (!this.statusElement) return;
		clearTimeout(this.liveRegionTimer);
		this.statusElement.setAttribute("aria-hidden", "false");
		this.statusElement.textContent = statusText;

		this.liveRegionTimer = setTimeout(() => {
			this.statusElement.setAttribute("aria-hidden", "true");
		}, 1000);
	}

	// renderSearchResults(resultsMarkup) {
	// 	this.predictiveSearchResults.innerHTML = resultsMarkup;
	// 	this.setAttribute("results", true);

	// 	this.setLiveRegionResults();
	// 	this.open();
	// 	if (this.promoModal && this.classList.contains("search-modal__form")) {
	// 		this.promoModal.classList.add("search__modal--hidden");
	// 	}
	// 	if (this.collectionList) {
	// 		this.collectionList.classList.add("hidden");
	// 	}
	// }


	renderSearchResults(resultsMarkup) {
		this.predictiveSearchResults.innerHTML = resultsMarkup;
		// 页头和搜索页各有一个实例；同一份响应里的 id 要按输入框隔离，
		// 否则 aria-controls / aria-activedescendant 会指到另一个实例。
		const ids = new Map();
		this.predictiveSearchResults.querySelectorAll("[id]").forEach((element) => {
			const previous = element.id;
			element.id = `${this.input.id}-${previous}`;
			ids.set(previous, element.id);
		});
		this.predictiveSearchResults.querySelectorAll("*").forEach((element) => {
			for (const attribute of ["aria-labelledby", "aria-describedby", "aria-controls", "aria-activedescendant", "for"]) {
				if (!element.hasAttribute(attribute)) continue;
				const references = element.getAttribute(attribute).split(/\s+/).map((id) => ids.get(id) || id);
				element.setAttribute(attribute, references.join(" "));
			}
			for (const attribute of ["href", "xlink:href"]) {
				const reference = element.getAttribute(attribute);
				if (reference?.startsWith("#") && ids.has(reference.slice(1))) {
					element.setAttribute(attribute, `#${ids.get(reference.slice(1))}`);
				}
			}
		});
		if (typeof colorSwatches === 'function') colorSwatches(this.predictiveSearchResults);
		this.setAttribute("results", true);

		// 换了一批结果，旧的选中项和它的 id 都不存在了
		this.input.setAttribute("aria-activedescendant", "");

		// 这里曾经有一段“标题以关键词开头的排前面”的客户端排序。它会把
		// 短标题的边角集合（如 rod-uk）顶到第一位，而英文商品标题关键词几乎
		// 都在词尾，等于系统性地把正经商品往下压。Shopify 自身的相关性排序更好，
		// 交给服务端，不要在这里二次排序。

		this.setLiveRegionResults();
		this.open();
		if (this.promoModal && this.classList.contains("search-modal__form")) {
			this.promoModal.classList.add("search__modal--hidden");
		}
		if (this.collectionList) {
			this.collectionList.classList.add("hidden");
		}
	}



	clickSearchTabs() {
		const results = this.querySelectorAll(".predictive-search__results-list");
		if (results.length != 0) {
			results[0].classList.add("active");
			this.querySelector(".predictive-search__result-tab").classList.add(
				"active",
			);
			this.querySelectorAll(".predictive-search__result-tab").forEach((tab) => {
				tab.addEventListener("click", (event) => {
					event.preventDefault();
					const typeTarget = tab.dataset.typeTarget;
					this.querySelectorAll(".predictive-search__result-tab").forEach(
						(element) => {
							element.classList.remove("active");
						},
					);
					tab.classList.add("active");

					results.forEach((element) => {
						let resultsType = element.dataset.type;
						if (resultsType == typeTarget) {
							element.classList.add("active");
						} else {
							element.classList.remove("active");
						}
					});
				});
			});
		}
	}

	hidePromoBlock() {
		if (this.classList.contains("search-modal__form")) {
			if (this.promoModal) {
				this.promoModal.classList.add("search__modal--hidden");
			}
			if (this.collectionList) {
				this.collectionList.classList.add("hidden");
			}
		}
	}

	showPromoBlock() {
		if (this.classList.contains("search-modal__form")) {
			if (this.promoModal) {
				this.promoModal.classList.remove("search__modal--hidden");
			}
			if (this.collectionList) {
				this.collectionList.classList.remove("hidden");
			}
		}
	}

	setLiveRegionResults() {
		this.removeAttribute("loading");
		this.setLiveRegionText(
			this.querySelector("[data-predictive-search-live-region-count-value]")
				?.textContent || '',
		);
	}

	open() {
		this.setAttribute("open", true);
		const popup = this.predictiveSearchResults.querySelector('[role="listbox"]');
		if (popup) this.input.setAttribute("aria-controls", popup.id);
		else this.input.removeAttribute("aria-controls");
		this.input.setAttribute("aria-expanded", Boolean(popup));
		this.isOpen = true;
		if (this.getQuery().length) {
			this.hidePromoBlock();
		} else {
			this.showPromoBlock();
		}
	}

	close(clearSearchTerm = false) {
		this.closeResults(clearSearchTerm);
		this.isOpen = false;
	}

	closeResults(clearSearchTerm = false) {
		clearTimeout(this.searchRequestTimer);
		// 关闭、清空和失焦后，迟到的响应不能重新打开下拉框。
		this.requestId++;
		this.abortController.abort();
		if (clearSearchTerm) {
			this.input.value = "";
			this.removeAttribute("results");
		}
		const selected = this.querySelector('[aria-selected="true"]');

		if (selected) selected.setAttribute("aria-selected", false);

		this.input.setAttribute("aria-activedescendant", "");
		this.removeAttribute("loading");
		this.removeAttribute("open");
		this.input.setAttribute("aria-expanded", false);
		this.input.removeAttribute("aria-controls");
		this.predictiveSearchResults.removeAttribute("style");
		if (this.getQuery().length) {
			this.hidePromoBlock();
		} else {
			this.showPromoBlock();
		}
	}

	disconnectedCallback() {
		clearTimeout(this.searchRequestTimer);
		this.requestId++;
		this.abortController.abort();
		clearTimeout(this.liveRegionTimer);
	}
}

customElements.define("predictive-search", PredictiveSearch);
